import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateUser, usersQueryKeys, type UpdateUserData, type User } from '@api/users';
import { ApiError } from '@api/client';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from '../useTranslation';

type Translate = (key: string, options?: Record<string, string>) => string;

function updateSuccessMessage(
  t: Translate,
  sent: UpdateUserData,
  saved: User,
  previous: User | undefined,
): string {
  const name = saved.name || saved.email;

  if (sent.status && sent.status !== previous?.status) {
    if (saved.status === 'verified') return t('update.activated', { name });
    if (saved.status === 'closed') return t('update.deactivated', { name });
    if (previous) {
      return t('update.status_changed', {
        name,
        from: t(`tabs.${previous.status}`),
        to: t(`tabs.${saved.status}`),
      });
    }
  }

  if (previous && saved.role !== previous.role) {
    return t('update.role_changed', {
      name,
      from: t(`roles.${previous.role}`),
      to: t(`roles.${saved.role}`),
    });
  }

  return t('update.success', { name });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: updateUser,
    onMutate: (variables) => ({
      previous: queryClient.getQueryData<User>(usersQueryKeys.detail(variables.id)),
    }),
    onSuccess: (response, variables, context) => {
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.lists() });
      queryClient.setQueryData(usersQueryKeys.detail(variables.id), response.user);

      toast({
        description: updateSuccessMessage(t, variables, response.user, context?.previous),
      });
    },
    onError: (error) => {
      // Field-level 422s render inline on the form.
      if (error instanceof ApiError && error.fieldErrors) return;
      toast({
        description: error.message || t('update.error_message'),
        variant: 'destructive',
      });
    },
  });
}
